/**
 * @fileoverview Unified Notification Center API client.
 *
 * Talks to the backend aggregator that merges customer hot-actions,
 * Co-Gérant proactive events and Co-Gérant alerts into one feed.
 *
 * @module api/notifications.api
 */

import { apiGet, apiPost, getRestaurantId } from './client';

const base = (rid) => {
  const id = rid ?? getRestaurantId();
  // Never call `/restaurant/undefined/...` (a logged-out or not-yet-loaded session).
  if (id === undefined || id === null || id === '') throw new Error('No restaurant selected');
  return `/admin/restaurant/${id}/notifications`;
};

/**
 * Fetch the unified notification feed.
 * @param {Object} opts
 * @param {'all'|'unread'|'read'} [opts.filter='all']
 * @param {number} [opts.limit=30]    page size (the backend caps it at 200)
 * @param {number} [opts.offset=0]    first item of the page; the answer carries
 *                                    `has_more` and `next_offset` for the next one
 * @param {number} [opts.restaurantId]
 */
export const getNotifications = async ({ filter = 'all', limit = 30, offset = 0, restaurantId } = {}) =>
  apiGet(base(restaurantId), { params: { filter, limit, offset } }).then((r) => r.data);

/** Fetch only the unread count (lightweight, for the bell badge). */
export const getUnreadCount = async (restaurantId) =>
  apiGet(`${base(restaurantId)}/unread-count`).then((r) => r.data?.unread_count ?? 0);

/** Mark a single notification read. uid is e.g. "hot_action:12", "proactive:5". */
export const markNotificationRead = async (uid, restaurantId) =>
  apiPost(`${base(restaurantId)}/${encodeURIComponent(uid)}/read`, {}).then((r) => r.data);

/** Mark every unread notification read for the restaurant. */
export const markAllNotificationsRead = async (restaurantId) =>
  apiPost(`${base(restaurantId)}/read-all`, {}).then((r) => r.data);
